import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type ReactElement,
} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {renderErrorToast, renderInfoToast} from "@khan/component-toast";

import {
    useSetTeacherCourses,
    useSetTeacherGradeLevels,
} from "./grade-level-and-course-mutations.ts";
import {
    useTeacherCourses,
    useTeacherGradeLevels,
} from "./grade-level-and-course-queries.ts";
import {
    CourseDropdown,
    GradeLevelsDropdown,
} from "./grade-levels-and-subjects-selectors.tsx";
import {strings} from "./strings.ts";
import type {TeacherCourse, TeacherGradeLevel} from "./types.ts";
import {arraysEqual} from "./utils.ts";

type Props = {
    kaid: string;
};

/**
 *Component for the Grade Levels and Subjects section of the settings page.
 */
export default function GradeLevelsAndSubjectsSection({
    kaid,
}: Props): ReactElement {
    const gradeLevelsResult = useTeacherGradeLevels(kaid);
    const coursesResult = useTeacherCourses(kaid);

    // Track loading states
    const isLoading =
        gradeLevelsResult.status === "loading" ||
        coursesResult.status === "loading";

    // Memoize the query results so that they remain stable between renders.
    const gradeLevels = useMemo(() => {
        return gradeLevelsResult.status === "success"
            ? gradeLevelsResult.gradeLevels
            : [];
    }, [gradeLevelsResult]);

    const courses = useMemo(() => {
        return coursesResult.status === "success" ? coursesResult.courses : [];
    }, [coursesResult]);

    // Maintain previous and current selection states
    const [prevGradeSelections, setPrevGradeSelections] = useState<
        TeacherGradeLevel[]
    >([]);
    const [currentGradeSelections, setCurrentGradeSelections] = useState<
        TeacherGradeLevel[]
    >([]);
    const [prevCourseSelections, setPrevCourseSelections] = useState<
        TeacherCourse[]
    >([]);
    const [currentCourseSelections, setCurrentCourseSelections] = useState<
        TeacherCourse[]
    >([]);

    useEffect(() => {
        if (gradeLevelsResult.status === "success") {
            setPrevGradeSelections(gradeLevels);
            setCurrentGradeSelections(gradeLevels);
        }
    }, [gradeLevelsResult.status, gradeLevels]);

    useEffect(() => {
        if (coursesResult.status === "success") {
            setPrevCourseSelections(courses);
            setCurrentCourseSelections(courses);
        }
    }, [coursesResult.status, courses]);

    // Mutation hooks
    const setTeacherGradeLevels = useSetTeacherGradeLevels();
    const setTeacherCourses = useSetTeacherCourses();

    // Change handlers
    const handleGradeLevelsChange = useCallback(
        (selectedValues: TeacherGradeLevel[]) => {
            setCurrentGradeSelections(selectedValues);
        },
        [],
    );

    const handleCoursesChange = useCallback(
        (selectedValues: TeacherCourse[]) => {
            setCurrentCourseSelections(selectedValues);
        },
        [],
    );

    const hasChanges = useMemo(() => {
        return (
            !arraysEqual(prevGradeSelections, currentGradeSelections) ||
            !arraysEqual(prevCourseSelections, currentCourseSelections)
        );
    }, [
        prevGradeSelections,
        currentGradeSelections,
        prevCourseSelections,
        currentCourseSelections,
    ]);

    const [isSaving, setIsSaving] = useState(false);
    const handleSave = useCallback(async () => {
        if (!hasChanges || isLoading) {
            return;
        }

        setIsSaving(true);
        try {
            // Only update if values have changed
            if (!arraysEqual(prevGradeSelections, currentGradeSelections)) {
                await setTeacherGradeLevels({
                    kaid,
                    gradeLevels:
                        currentGradeSelections as ReadonlyArray<TeacherGradeLevel>,
                });
                setPrevGradeSelections(currentGradeSelections);
            }

            if (!arraysEqual(prevCourseSelections, currentCourseSelections)) {
                await setTeacherCourses({
                    kaid,
                    courses:
                        currentCourseSelections as ReadonlyArray<TeacherCourse>,
                });
                setPrevCourseSelections(currentCourseSelections);
            }
            renderInfoToast(t`Changes saved`);
        } catch (error) {
            renderErrorToast(t`Failed to save changes`);
            return error;
        } finally {
            setIsSaving(false);
        }
    }, [
        hasChanges,
        isLoading,
        prevGradeSelections,
        currentGradeSelections,
        prevCourseSelections,
        currentCourseSelections,
        setTeacherGradeLevels,
        kaid,
        setTeacherCourses,
    ]);

    // Handle error states
    if (
        gradeLevelsResult.status === "error" ||
        coursesResult.status === "error"
    ) {
        renderErrorToast(t`Failed to load data`);
        return <></>;
    }

    return (
        <View role="region" aria-label={t`Grade levels and subjects settings`}>
            <HeadingSmall tag="h2">
                {strings.gradeLevelsAndSubjects()}
            </HeadingSmall>
            <Strut size={spacing.large_24} />
            <GradeLevelsDropdown
                selectedValues={currentGradeSelections}
                onChange={handleGradeLevelsChange}
            />
            <Strut size={spacing.medium_16} />
            <CourseDropdown
                selectedValues={currentCourseSelections}
                onChange={handleCoursesChange}
            />
            <Strut size={spacing.xxLarge_48 - spacing.xSmall_8} />
            <Button
                kind="primary"
                style={styles.button}
                testId="save-grade-level-and-course-changes"
                disabled={!hasChanges || isLoading || isSaving}
                onClick={handleSave}
            >
                {strings.saveChanges()}
            </Button>
        </View>
    );
}

const styles = StyleSheet.create({
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
});
